import { Component } from 'react';

/** Erreurs DOM typiques d'une traduction automatique du navigateur (Google Translate
 *  réécrit le DOM sous React) : on recharge alors la page automatiquement. */
const isTranslationDomError = (error) => {
  const msg = `${error?.name || ''} ${error?.message || ''}`;
  return /removeChild|insertBefore|NotFoundError|n'est pas un enfant de ce n/i.test(msg);
};

const RELOAD_FLAG = 'crm_dom_auto_reload_at';

export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, autoReloading: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error, autoReloading: isTranslationDomError(error) };
  }

  componentDidCatch(error, info) {
    console.error('[ErrorBoundary]', error, info);

    if (!isTranslationDomError(error)) return;

    // Garde anti-boucle : un seul rechargement automatique par minute
    const last = Number(sessionStorage.getItem(RELOAD_FLAG) || 0);
    if (Date.now() - last < 60000) {
      this.setState({ autoReloading: false });
      return;
    }
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
    window.setTimeout(() => window.location.reload(), 800);
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    const translationIssue = isTranslationDomError(this.state.error);

    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4 p-8 text-center notranslate" data-testid="error-boundary">
        <div className="text-4xl">⚠️</div>
        <h2 className="text-xl font-semibold">
          {translationIssue ? 'Traduction automatique détectée' : 'Une erreur est survenue'}
        </h2>
        <p className="text-sm text-muted-foreground max-w-md">
          {translationIssue ? (
            <>
              Le CRM est déjà en français : la traduction automatique du navigateur
              perturbe l'affichage.
              {this.state.autoReloading
                ? ' La page se recharge automatiquement…'
                : ' Refuse la traduction proposée par le navigateur, puis recharge la page.'}
            </>
          ) : (
            <>
              Un problème inattendu a empêché le chargement de cette page.
              {this.state.error?.message && (
                <span className="block mt-2 font-mono text-xs bg-muted rounded px-2 py-1">
                  {this.state.error.message}
                </span>
              )}
            </>
          )}
        </p>
        <button
          onClick={() => window.location.reload()}
          data-testid="error-boundary-reload"
          className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
        >
          Recharger la page
        </button>
      </div>
    );
  }
}
